import { linkify, meetingLink, PLANS, type MeetingSummary, type PastMeeting, type TranscriptMessage, type UserProfile } from '@calling/shared';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarIcon, CopyIcon, LinkIcon, PlusIcon, ShareIcon } from '../components/icons';
import { ReportDialog } from '../components/ReportDialog';
import { Brand, Page } from '../components/Shell';
import { Avatar, Banner, Confirm, Dialog, Toasts, useToasts } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { clockParts, formatDuration, fromLocalInputs, localInputValues, people, relativeDay } from '../lib/format';
import { meetingLimitsText } from '../lib/planText';
import { copyText, isNative, PUBLIC_ORIGIN, saveTextFile, shareLink } from '../lib/platform';
import { JoinWithCode, useServerInfo } from './SignIn';

interface Meetings {
  upcoming: MeetingSummary[];
  recent: PastMeeting[];
}

const linkFor = (code: string) => meetingLink(PUBLIC_ORIGIN, code);

function message(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong. Try again.';
}

/** Copy and Share for a meeting link (E2-S2). */
export function useLinkActions(show: (text: string, tone?: 'info' | 'warning') => void) {
  const copy = async (code: string) => {
    show((await copyText(linkFor(code))) ? 'Link copied' : 'Could not copy. Select the link and copy it yourself.', undefined);
  };
  const share = async (code: string) => {
    const result = await shareLink(linkFor(code));
    if (result === 'copied') show('Link copied');
    else if (result === 'failed') show('Could not share. Copy the link instead.', 'warning');
  };
  return { copy, share };
}

function ScheduleDialog({
  existing,
  maxMinutes,
  onClose,
  onSaved,
}: {
  existing?: MeetingSummary;
  /** How long the person's plan lets a meeting run; null for no limit. */
  maxMinutes: number | null;
  onClose: () => void;
  onSaved: (room: MeetingSummary, created: boolean) => void;
}) {
  // Default: the next half hour, an hour from now.
  const initial = existing?.startsAt ? new Date(existing.startsAt) : new Date(Math.ceil((Date.now() + 3_600_000) / 1_800_000) * 1_800_000);
  const values = localInputValues(initial);
  const [title, setTitle] = useState(existing?.title ?? '');
  const [date, setDate] = useState(values.date);
  const [time, setTime] = useState(values.time);
  const [duration, setDuration] = useState(String(existing?.durationMin ?? 30));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const startsAt = fromLocalInputs(date, time);
    if (!title.trim()) return setError('Give the meeting a title.');
    if (!startsAt) return setError('Choose a date and a start time.');
    if (startsAt.getTime() < Date.now()) return setError('Choose a start time in the future.');
    setBusy(true);
    setError('');
    try {
      // The time is sent in UTC; everyone sees it in their own time zone (E2-S4).
      const body = { title, startsAt: startsAt.toISOString(), durationMin: Number(duration) };
      const res = existing
        ? await api.request<{ room: MeetingSummary }>(`/api/rooms/${existing.code}`, { method: 'PATCH', body })
        : await api.request<{ room: MeetingSummary }>('/api/rooms', { method: 'POST', body: { mode: 'scheduled', ...body } });
      onSaved(res.room, !existing);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={existing ? 'Edit meeting' : 'Schedule a meeting'} onClose={onClose} testId="schedule-dialog">
      <form className="stack" onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor="sched-title">Title</label>
          <input id="sched-title" className="input" maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Sprint planning" autoFocus />
        </div>
        <div className="row" style={{ alignItems: 'flex-start' }}>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="sched-date">Date</label>
            <input id="sched-date" className="input" type="date" value={date} min={localInputValues(new Date()).date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="sched-time">Start time</label>
            <input id="sched-time" className="input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="sched-duration">Duration</label>
          <select id="sched-duration" className="select" value={duration} onChange={(e) => setDuration(e.target.value)}>
            {[15, 30, 45, 60, 90, 120, 180].map((m) => (
              <option key={m} value={m}>
                {formatDuration(m * 60)}
              </option>
            ))}
          </select>
          <span className="hint">Times are shown in your time zone ({Intl.DateTimeFormat().resolvedOptions().timeZone}).</span>
          {maxMinutes !== null && Number(duration) > maxMinutes ? (
            <span className="hint" style={{ color: 'var(--warn-title)' }} data-testid="schedule-limit">
              Your plan ends a meeting after {maxMinutes} minutes. You can start it again from the same link.
            </span>
          ) : null}
        </div>
        {error ? (
          <span className="field error" role="alert" style={{ color: 'var(--danger-text)', fontSize: 13 }}>
            {error}
          </span>
        ) : null}
        <div className="dialog__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn--primary" disabled={busy}>
            {existing ? 'Save changes' : 'Schedule'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function LinkDialog({ room, onClose, show }: { room: MeetingSummary; onClose: () => void; show: (text: string, tone?: 'info' | 'warning') => void }) {
  const navigate = useNavigate();
  const { copy, share } = useLinkActions(show);
  return (
    <Dialog title="Your meeting link" onClose={onClose} testId="link-dialog">
      <p>Send this to the people you want to invite. They wait until you start the meeting and admit them.</p>
      <input className="input input--mono" style={{ fontSize: 14 }} readOnly value={linkFor(room.code)} aria-label="Meeting link" onFocus={(e) => e.currentTarget.select()} />
      <div className="dialog__actions">
        <button className="btn" onClick={() => copy(room.code)}>
          <CopyIcon size={18} /> Copy link
        </button>
        <button className="btn" onClick={() => share(room.code)}>
          <ShareIcon size={18} /> Share
        </button>
      </div>
      <button className="btn btn--primary btn--block" onClick={() => navigate(`/m/${room.code}`)}>
        Start the meeting now
      </button>
    </Dialog>
  );
}

interface Transcript {
  title: string;
  startedAt: string;
  messages: TranscriptMessage[];
}

/** The chat saved with a past meeting: read it, download it, or delete it. */
function TranscriptDialog({ meeting, onClose, onDeleted, show }: { meeting: PastMeeting; onClose: () => void; onDeleted: () => void; show: (text: string, tone?: 'info' | 'warning') => void }) {
  const [data, setData] = useState<Transcript | null>(null);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [reporting, setReporting] = useState<TranscriptMessage | null>(null);
  useEffect(() => {
    api
      .request<Transcript>(`/api/meetings/${meeting.id}/chat`)
      .then(setData)
      .catch((err) => setError(message(err)));
  }, [meeting.id]);

  const time = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const download = () => {
    if (!data) return;
    const lines = [`${data.title} · ${new Date(data.startedAt).toLocaleString()}`, '', ...data.messages.map((m) => `[${time(m.at)}] ${m.name}: ${m.text}`)];
    void saveTextFile(`chat-${meeting.code}.txt`, 'text/plain', lines.join('\n'));
  };

  if (confirming) {
    return (
      <Confirm
        title="Delete this chat?"
        body="The messages from this meeting will be removed for everyone. This cannot be undone."
        confirmLabel="Delete chat"
        danger
        onCancel={() => setConfirming(false)}
        onConfirm={async () => {
          try {
            await api.request(`/api/meetings/${meeting.id}/chat`, { method: 'DELETE' });
            show('Chat deleted');
            onDeleted();
          } catch (err) {
            show(message(err), 'warning');
            setConfirming(false);
          }
        }}
      />
    );
  }
  return (
    <>
      <Dialog title={`Chat · ${meeting.title}`} onClose={onClose} testId="transcript">
        {error ? <Banner tone="danger" title="The chat could not be loaded">{error}</Banner> : null}
        {!data && !error ? <p>Loading…</p> : null}
        <div className="stack" role="log" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {data?.messages.map((m) => (
            <div key={m.id} className="stack stack--tight" style={{ gap: 2 }} data-testid="transcript-message">
              <span className="muted" style={{ fontSize: 12 }}>
                <b style={{ color: 'var(--text)', fontSize: 13 }}>{m.mine ? 'You' : m.name}</b> · {time(m.at)}
                {/* Anyone else's message can be reported to the people who run the service; its author isn't told who. */}
                {m.mine ? null : (
                  <>
                    {' · '}
                    <button className="link-btn" style={{ fontSize: 12 }} aria-label={`Report this message from ${m.name}`} onClick={() => setReporting(m)}>
                      Report
                    </button>
                  </>
                )}
              </span>
              <span style={{ fontSize: 14, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {linkify(m.text).map((part, i) =>
                  part.type === 'link' ? (
                    <a key={i} href={part.href} target="_blank" rel="noopener noreferrer nofollow">
                      {part.text}
                    </a>
                  ) : (
                    part.text
                  ),
                )}
              </span>
            </div>
          ))}
        </div>
        {data ? (
          <div className="dialog__actions">
            {/* Only the person whose meeting it was may delete its chat. */}
            {meeting.owned ? (
              <button className="btn btn--ghost" style={{ color: 'var(--danger-text)' }} onClick={() => setConfirming(true)}>
                Delete chat
              </button>
            ) : null}
            <button className="btn" onClick={download}>
              Download
            </button>
          </div>
        ) : null}
      </Dialog>
      {reporting ? (
        <ReportDialog
          subject={{ kind: 'message', name: reporting.name, text: reporting.text }}
          send={async (input) => {
            await api.request(`/api/meetings/${meeting.id}/chat/${reporting.id}/report`, { method: 'POST', body: { reason: input.reason, ...(input.details ? { details: input.details } : {}) } });
          }}
          onSent={() => {
            setReporting(null);
            show('Report sent. Thank you.');
          }}
          onClose={() => setReporting(null)}
        />
      ) : null}
    </>
  );
}

function subtitle(room: MeetingSummary): string {
  if (!room.startsAt) return room.status === 'live' ? 'In progress' : 'Link ready to share';
  const when = relativeDay(new Date(room.startsAt));
  return `${room.status === 'live' ? 'In progress' : when} · ${formatDuration((room.durationMin ?? 30) * 60)}`;
}

export default function Home({ user }: { user: UserProfile }) {
  const navigate = useNavigate();
  const { toasts, show, dismiss } = useToasts();
  const { copy, share } = useLinkActions(show);
  const emailEnabled = useServerInfo().info?.emailEnabled ?? false;
  const [meetings, setMeetings] = useState<Meetings | null>(null);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<{ text: string; retry: () => void } | null>(null);
  const [menu, setMenu] = useState(false);
  const [scheduling, setScheduling] = useState<MeetingSummary | 'new' | null>(null);
  const [linkRoom, setLinkRoom] = useState<MeetingSummary | null>(null);
  const [detail, setDetail] = useState<MeetingSummary | null>(null);
  const [cancelling, setCancelling] = useState<MeetingSummary | null>(null);
  const [transcript, setTranscript] = useState<PastMeeting | null>(null);

  const load = useCallback(async () => {
    try {
      // One request for both lists (E2-S5).
      setMeetings(await api.request<Meetings>('/api/rooms'));
      setLoadError('');
    } catch (err) {
      setLoadError(message(err));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const create = async (mode: 'instant' | 'link') => {
    setBusy(true);
    setCreateError(null);
    try {
      const { room } = await api.request<{ room: MeetingSummary }>('/api/rooms', { method: 'POST', body: { mode } });
      if (mode === 'instant') navigate(`/m/${room.code}`);
      else {
        setLinkRoom(room);
        void load();
      }
    } catch (err) {
      // Nothing was created; offer to try again (E2-S1).
      setCreateError({ text: err instanceof ApiError ? err.message : "Couldn't create the meeting. Check your connection.", retry: () => void create(mode) });
    } finally {
      setBusy(false);
    }
  };

  const downloadIcs = async (room: MeetingSummary) => {
    try {
      const ics = await api.request<string>(`/api/rooms/${room.code}/ics`);
      await saveTextFile(`meeting-${room.code}.ics`, 'text/calendar', ics);
    } catch (err) {
      show(message(err), 'warning');
    }
  };

  return (
    <Page tabs>
      <header className="app-header">
        <Brand />
        <Link to="/settings" aria-label={`Profile and settings for ${user.name}`} style={{ textDecoration: 'none' }}>
          <Avatar name={user.name} id={user.id} />
        </Link>
      </header>

      <section className="stack">
        <h1 className="hero">Start a call, share a link, admit who you want.</h1>
        <div className="row" style={{ marginTop: 8 }}>
          <button className="btn btn--primary btn--grow" disabled={busy} onClick={() => setMenu(true)}>
            <PlusIcon size={20} /> New meeting
          </button>
          <button className="btn" disabled={busy} onClick={() => create('link')}>
            <LinkIcon size={18} /> Get link
          </button>
        </div>
        {createError ? (
          <Banner
            tone="danger"
            title="The meeting wasn't created"
            action={
              <button className="btn" onClick={createError.retry}>
                Try again
              </button>
            }
          >
            {createError.text}
          </Banner>
        ) : null}
        {user.plan === 'free' ? (
          <p className="note" data-testid="plan-note">
            Free plan: meetings of {meetingLimitsText(PLANS.free.limits)}.{isNative ? null : <> <Link to="/billing">See plans</Link></>}
          </p>
        ) : null}
        <JoinWithCode />
      </section>

      {loadError ? (
        <Banner
          tone="danger"
          title="Your meetings could not be loaded"
          action={
            <button className="btn" onClick={() => void load()}>
              Try again
            </button>
          }
        >
          {loadError}
        </Banner>
      ) : null}

      <section className="stack" aria-labelledby="upcoming-title">
        <h2 className="section-title" id="upcoming-title">
          Upcoming
        </h2>
        {meetings && meetings.upcoming.length === 0 ? <p className="muted" style={{ fontSize: 14 }}>Nothing scheduled. Meetings you schedule and links you create appear here.</p> : null}
        {meetings?.upcoming.map((room, index) => {
          const clock = room.startsAt ? clockParts(new Date(room.startsAt)) : null;
          return (
            <div className="meeting-card" key={room.code} data-testid="upcoming-meeting">
              <div className="meeting-card__time">
                {clock ? (
                  <>
                    <b>{clock.time}</b>
                    <span>{clock.meridiem}</span>
                  </>
                ) : (
                  <LinkIcon size={20} />
                )}
              </div>
              <button className="meeting-card__body" onClick={() => setDetail(room)} aria-label={`${room.title}: options`}>
                <b>{room.title}</b>
                <span>{subtitle(room)}</span>
              </button>
              {index === 0 || room.status === 'live' ? (
                <button className="btn btn--primary btn--medium" onClick={() => navigate(`/m/${room.code}`)}>
                  {room.status === 'live' ? 'Join' : 'Start'}
                </button>
              ) : (
                <button className="icon-btn" aria-label={`Copy link for ${room.title}`} onClick={() => copy(room.code)}>
                  <CopyIcon size={18} />
                </button>
              )}
            </div>
          );
        })}
      </section>

      <section className="stack" aria-labelledby="recent-title">
        <h2 className="section-title" id="recent-title">
          Recent
        </h2>
        {meetings && meetings.recent.length === 0 ? <p className="muted" style={{ fontSize: 14 }}>Meetings you have hosted, or joined while signed in, appear here once they end.</p> : null}
        <div>
          {meetings?.recent.map((m) => (
            <div className="recent-row" key={m.id} data-testid="recent-meeting">
              <div>
                <b>{m.title}</b>
                <span>
                  {relativeDay(new Date(m.startedAt))} · {formatDuration(m.durationSec)} · {people(m.participantCount)}
                </span>
              </div>
              <div className="row" style={{ gap: 8, flexShrink: 0 }}>
                {m.chatCount > 0 ? (
                  <button className="btn btn--small btn--ghost" onClick={() => setTranscript(m)} aria-label={`Chat from ${m.title}: ${m.chatCount} messages`}>
                    Chat ({m.chatCount})
                  </button>
                ) : null}
                {/* A recording belongs to whoever made it: only the meeting's own recordings are a link away. */}
                {m.recorded && m.owned ? (
                  <Link to="/recordings" className="rec-badge rec-badge--small rec-badge--plain" style={{ textDecoration: 'none' }} aria-label="Recorded: open recordings">
                    REC
                  </Link>
                ) : m.recorded ? (
                  <span className="rec-badge rec-badge--small rec-badge--plain" title="This meeting was recorded">
                    REC
                  </span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </section>

      {menu ? (
        <Dialog title="New meeting" onClose={() => setMenu(false)}>
          <div className="menu">
            <button
              onClick={() => {
                setMenu(false);
                void create('instant');
              }}
            >
              <PlusIcon size={20} /> Start now
            </button>
            <button
              onClick={() => {
                setMenu(false);
                void create('link');
              }}
            >
              <LinkIcon size={20} /> Get a link to share
            </button>
            <button
              onClick={() => {
                setMenu(false);
                setScheduling('new');
              }}
            >
              <CalendarIcon size={20} /> Schedule for later
            </button>
          </div>
        </Dialog>
      ) : null}

      {scheduling ? (
        <ScheduleDialog
          existing={scheduling === 'new' ? undefined : scheduling}
          maxMinutes={user.plan ? PLANS[user.plan].limits.maxMeetingMinutes : null}
          onClose={() => setScheduling(null)}
          onSaved={(room, created) => {
            setScheduling(null);
            void load();
            if (created) {
              setLinkRoom(room);
              if (user.emailVerified && emailEnabled) show('Scheduled. We emailed you a calendar invite.');
              else show('Scheduled. Use “Add to calendar” to save it.');
            } else show('Meeting updated');
          }}
        />
      ) : null}

      {linkRoom ? <LinkDialog room={linkRoom} onClose={() => setLinkRoom(null)} show={show} /> : null}

      {detail ? (
        <Dialog title={detail.title} onClose={() => setDetail(null)} testId="meeting-detail">
          <p className="mono" style={{ fontSize: 13 }}>
            {detail.code}
            {detail.startsAt ? ` · ${new Date(detail.startsAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}` : ''}
          </p>
          <div className="menu">
            <button onClick={() => navigate(`/m/${detail.code}`)}>
              <PlusIcon size={20} /> {detail.status === 'live' ? 'Join meeting' : 'Start meeting'}
            </button>
            <button onClick={() => copy(detail.code)}>
              <CopyIcon size={20} /> Copy link
            </button>
            <button onClick={() => share(detail.code)}>
              <ShareIcon size={20} /> Share
            </button>
            {detail.kind === 'scheduled' ? (
              <>
                <button onClick={() => downloadIcs(detail)}>
                  <CalendarIcon size={20} /> Add to calendar
                </button>
                <button
                  onClick={() => {
                    setScheduling(detail);
                    setDetail(null);
                  }}
                >
                  <CalendarIcon size={20} /> Edit
                </button>
              </>
            ) : null}
            <button
              data-danger="true"
              onClick={() => {
                setCancelling(detail);
                setDetail(null);
              }}
            >
              {detail.kind === 'scheduled' ? 'Cancel meeting' : 'Delete link'}
            </button>
          </div>
        </Dialog>
      ) : null}

      {cancelling ? (
        <Confirm
          title={cancelling.kind === 'scheduled' ? 'Cancel this meeting?' : 'Delete this link?'}
          body={`Anyone who opens the link for “${cancelling.title}” will see “Cancelled by host”. This cannot be undone.`}
          confirmLabel={cancelling.kind === 'scheduled' ? 'Cancel meeting' : 'Delete link'}
          danger
          onCancel={() => setCancelling(null)}
          onConfirm={async () => {
            try {
              await api.request(`/api/rooms/${cancelling.code}`, { method: 'DELETE' });
              show(cancelling.kind === 'scheduled' ? 'Meeting cancelled' : 'Link deleted');
              await load();
            } catch (err) {
              show(message(err), 'warning');
            }
            setCancelling(null);
          }}
        />
      ) : null}

      {transcript ? (
        <TranscriptDialog
          meeting={transcript}
          show={show}
          onClose={() => setTranscript(null)}
          onDeleted={() => {
            setTranscript(null);
            void load();
          }}
        />
      ) : null}

      <Toasts items={toasts} onDismiss={dismiss} />
    </Page>
  );
}
